import { Close } from '@carbon/react/icons';
import { Button, Loading, Search } from '@carbon/react';
import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { debounce } from 'lodash-es';
import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer';
import { EmptyState } from 'components/empty-state';
import { useInfiniteScroll } from 'lib/hooks/useInfiniteScroll';
import style from './list-drawer.module.scss';

export interface ListDrawerProps<Item> {
  open: boolean;
  direction?: 'left' | 'right';
  size?: string | number;
  onClose: () => void;
  title: string;
  subtitle?: (totalCount: number) => string;

  loader: (args: {
    text: string;
    page: number;
    pageSize: number;
  }) => Promise<{ results: Item[]; totalCount: number }>;
  pageSize?: number;

  renderItem: (item: Item) => ReactNode;
  getItemKey: (item: Item) => string | number;
  search?: { placeholder?: string; label?: string; debounceMs?: number };

  resetOnOpen?: boolean;

  emptyText: string;
  emptySearchText: string;
  errorText: string;
}

type Phase = 'idle' | 'loading-first' | 'has-data' | 'error-first';
type LoadMoreStatus = 'idle' | 'in-flight' | 'error';

interface State<Item> {
  accumulated: Item[];
  totalCount: number | null;
  currentPage: number;
  phase: Phase;
  text: string;
  loadMoreStatus: LoadMoreStatus;
}

type Action<Item> =
  | { type: 'fetchFirstStart' }
  | { type: 'resetWithText'; text: string }
  | { type: 'fetchFirstSuccess'; results: Item[]; totalCount: number }
  | { type: 'fetchFirstError' }
  | { type: 'loadMoreStart' }
  // page is explicit so Try-again can replay the failed page rather than blindly incrementing.
  | {
      type: 'loadMoreSuccess';
      page: number;
      results: Item[];
      totalCount: number;
    }
  | { type: 'loadMoreError' };

export const ListDrawer = <Item,>({
  open,
  direction = 'right',
  size,
  onClose,
  title,
  subtitle,
  loader,
  pageSize = 25,
  renderItem,
  getItemKey,
  search,
  resetOnOpen = true,
  emptyText,
  emptySearchText,
  errorText,
}: ListDrawerProps<Item>) => {
  const [state, dispatch] = useReducer(
    (current: State<Item>, action: Action<Item>): State<Item> => {
      switch (action.type) {
        case 'fetchFirstStart':
          return {
            ...current,
            phase: 'loading-first',
            loadMoreStatus: 'idle',
          };
        case 'resetWithText':
          return {
            ...current,
            phase: 'loading-first',
            text: action.text,
            accumulated: [],
            currentPage: 1,
            loadMoreStatus: 'idle',
          };
        case 'fetchFirstSuccess':
          return {
            ...current,
            phase: 'has-data',
            accumulated: action.results,
            totalCount: action.totalCount,
            currentPage: 1,
            loadMoreStatus: 'idle',
          };
        case 'fetchFirstError':
          return { ...current, phase: 'error-first' };
        case 'loadMoreStart':
          return { ...current, loadMoreStatus: 'in-flight' };
        case 'loadMoreSuccess':
          return {
            ...current,
            accumulated: [...current.accumulated, ...action.results],
            totalCount: action.totalCount,
            currentPage: action.page,
            loadMoreStatus: 'idle',
          };
        case 'loadMoreError':
          return { ...current, loadMoreStatus: 'error' };
      }
    },
    undefined,
    (): State<Item> => ({
      accumulated: [],
      totalCount: null,
      currentPage: 1,
      phase: 'idle',
      text: '',
      loadMoreStatus: 'idle',
    })
  );
  const generationRef = useRef(0);
  const prevOpenRef = useRef(false);
  const prevLoaderRef = useRef(loader);
  const needsRefreshOnNextOpenRef = useRef(false);
  // In-flight typed value (echoed to the input); state.text is the committed/fetched value.
  const [searchInput, setSearchInput] = useState('');

  const runFetch = useCallback(
    async (text: string, resetItems: boolean) => {
      const requestGen = ++generationRef.current;
      dispatch(
        resetItems
          ? { type: 'resetWithText', text }
          : { type: 'fetchFirstStart' }
      );
      try {
        const response = await loader({ text, page: 1, pageSize });
        if (requestGen !== generationRef.current) return;
        dispatch({
          type: 'fetchFirstSuccess',
          results: response.results,
          totalCount: response.totalCount,
        });
      } catch {
        if (requestGen !== generationRef.current) return;
        dispatch({ type: 'fetchFirstError' });
      }
    },
    [loader, pageSize]
  );

  // Loader-identity change: treat as new search context. If the same commit is
  // also opening the drawer, defer to the open-transition effect (avoids double fetch).
  useEffect(() => {
    if (prevLoaderRef.current === loader) return;
    prevLoaderRef.current = loader;
    if (open && prevOpenRef.current) {
      runFetch(state.text, true);
    } else {
      needsRefreshOnNextOpenRef.current = true;
    }
    // Intentional: state.text + open + runFetch read from the closure at loader-change time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader]);

  // Open transition (false → true). Subsumes the first-ever-open fetch.
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || wasOpen) return;
    debouncedSearch.cancel();
    if (resetOnOpen) {
      needsRefreshOnNextOpenRef.current = false;
      setSearchInput('');
      runFetch('', true);
    } else if (needsRefreshOnNextOpenRef.current) {
      needsRefreshOnNextOpenRef.current = false;
      runFetch(state.text, true);
    } else if (state.phase === 'idle') {
      runFetch('', false);
    }
    // Intentional: state.text + state.phase + runFetch + debouncedSearch read from the closure at transition time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resetOnOpen]);

  const debouncedSearch = useMemo(
    () =>
      debounce((newText: string) => {
        runFetch(newText, true);
      }, search?.debounceMs ?? 400),
    [search?.debounceMs, runFetch]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newText = event.target.value;
    setSearchInput(newText);
    debouncedSearch(newText);
  };

  const runLoadMore = useCallback(async () => {
    if (state.loadMoreStatus === 'in-flight') return;
    const nextPage = state.currentPage + 1;
    const requestGen = ++generationRef.current;
    dispatch({ type: 'loadMoreStart' });
    try {
      const response = await loader({
        text: state.text,
        page: nextPage,
        pageSize,
      });
      if (requestGen !== generationRef.current) return;
      dispatch({
        type: 'loadMoreSuccess',
        page: nextPage,
        results: response.results,
        totalCount: response.totalCount,
      });
    } catch {
      if (requestGen !== generationRef.current) return;
      dispatch({ type: 'loadMoreError' });
    }
  }, [loader, pageSize, state.text, state.currentPage, state.loadMoreStatus]);

  const hasMore =
    state.totalCount !== null && state.accumulated.length < state.totalCount;
  const canAutoLoadMore = hasMore && state.loadMoreStatus !== 'error';

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: runLoadMore,
    hasMore: canAutoLoadMore,
    loading: state.loadMoreStatus === 'in-flight',
  });

  const showEmpty =
    state.phase === 'has-data' && state.totalCount === 0 && state.text === '';
  const showEmptySearch =
    state.phase === 'has-data' && state.totalCount === 0 && state.text !== '';
  const showList = state.phase === 'has-data' && state.accumulated.length > 0;

  return (
    <Drawer
      lockBackgroundScroll
      open={open}
      direction={direction}
      size={size}
      onClose={onClose}
    >
      <DrawerHeader
        className={classNames(style.headerRow, {
          [style.headerRowLeft]: direction === 'left',
        })}
      >
        <div className={style.headerText}>
          <h2 className={style.title}>{title}</h2>
          {subtitle && state.totalCount !== null && (
            <span className={style.subtitle}>{subtitle(state.totalCount)}</span>
          )}
        </div>
        <Button
          hasIconOnly
          size="lg"
          kind="ghost"
          onClick={onClose}
          iconDescription="Close"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={(props) => <Close size={20} {...props} />}
        />
      </DrawerHeader>
      <DrawerContent>
        {search && (
          <div className={style.searchRow}>
            <Search
              size="lg"
              labelText={search.label ?? 'Search'}
              placeholder={search.placeholder ?? ''}
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
        )}
        {state.phase === 'loading-first' && (
          <div className={style.loadingContainer}>
            <Loading description="Loading" withOverlay={false} small />
          </div>
        )}
        {state.phase === 'error-first' && (
          <EmptyState headerText={errorText}>
            <Button
              kind="tertiary"
              size="sm"
              onClick={() => runFetch(state.text, false)}
            >
              Try again
            </Button>
          </EmptyState>
        )}
        {showEmpty && <EmptyState headerText={emptyText} />}
        {showEmptySearch && <EmptyState headerText={emptySearchText} />}
        {showList && (
          <div className={style.list}>
            {state.accumulated.map((item) => (
              <div key={getItemKey(item)}>{renderItem(item)}</div>
            ))}
            <div ref={sentinelRef} className={style.sentinel} />
            {state.loadMoreStatus === 'in-flight' && (
              <div className={style.loadingMore}>
                <Loading
                  description="Loading more..."
                  withOverlay={false}
                  small
                />
              </div>
            )}
            {state.loadMoreStatus === 'error' && (
              <div className={style.loadMoreError}>
                <span>{errorText}</span>
                <Button kind="ghost" size="sm" onClick={runLoadMore}>
                  Try again
                </Button>
              </div>
            )}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};
